import { useRef } from "react";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { ChartTooltip } from "../ChartTooltip";
import { DocumentReviewSummary } from "../DocumentReviewSummary";
import {
  EVENT_LABEL_LINE_HEIGHT,
  EVENT_LABEL_ROOM,
  EVENT_PIN_HEAD_GAP,
  EVENT_PIN_HEIGHT,
  EventPin,
  EventPinLabel,
  PinLabel,
} from "../EventPin";
import { EventSummary } from "../EventSummary";
import {
  documentLabelOpacity,
  endOfReviewWindow,
  formatDate,
  formatReviewPeriod,
  interpolateDomain,
  isInDomain,
  lerp,
  placeEventPins,
  reviewLegs,
  reviewPeriodDomain,
  reviewWindowStatusOf,
  timeScale,
  weekTicks,
  yearDomain,
  yearLabelOpacity,
  type DocumentReview,
  type ReviewWindow,
  type ReviewWindowStatus,
} from "../helpers";
import { activateOnKey } from "../keyboard";
import {
  ReviewWindowStatusLegend,
  useWindowStatusLabel,
  windowStatusFill,
} from "../ReviewWindowStatusLegend";
import { ReviewWindowSummary } from "../ReviewWindowSummary";
import { useText } from "../text";
import { type AnnualWheelEvent, type ReviewWindowsChartProps } from "../types";
import { useChartTooltip } from "../useChartTooltip";
import { useElementWidth } from "../useElementWidth";
import { workflowStepColor } from "../WorkflowStepSwatch";
import { ReviewWindowDocuments } from "./ReviewWindowDocuments";
import {
  arcLine,
  formatReviewPeriodInYear,
  monthStarts,
  placeWheelLabels,
  polarPoint,
  toDegrees,
} from "./wheelHelpers";

const countFill: Record<ReviewWindowStatus, string> = {
  done: "fill-secondary-foreground",
  ongoing: "fill-primary-foreground",
  upcoming: "fill-primary-foreground",
};

const VIEW_RADIUS = 366;
const RING_RADIUS = 276;
// A band around the ring names the months, or the weeks of an opened window
const BAND_WIDTH = 26;
const BAND_RADIUS = RING_RADIUS + BAND_WIDTH;
const RING_PADDING = 8;
const LANE_THICKNESS = 34;
const LANE_GAP = 6;
const TICK_LENGTH = 8;
const TICK_LABEL_RADIUS = RING_RADIUS + BAND_WIDTH / 2;
// A week is named after its tick, this far along the band
const WEEK_LABEL_OFFSET = 5;
// The event pins stand on the band
const PIN_HEAD_DISTANCE = BAND_RADIUS + EVENT_PIN_HEIGHT;
// Where the labels of the pins and of today begin
const LABEL_RADIUS = PIN_HEAD_DISTANCE + 14;
// A narrower wheel has no room for labels beside it at a size that can be read
const MIN_LABELLED_WIDTH = 840;
// Today's label sits here, centred, on a wheel without the other labels
const TODAY_LABEL_RADIUS = 344;
const MIN_LABELLED_ARC_LENGTH = 28;
const WINDOW_CORNER_RADIUS = 4;
// How far along its lane the arrowhead of a review window reaches
const ARROWHEAD_LENGTH = 12;
const HUB_RADIUS = 84;
// The year with a button on each side, measured from the centre to its far corners
const YEAR_HUB_RADIUS = 132;
const YEAR_BUTTON_SIZE = 36;
// The wheel shrinks on small screens, so the button listens well beyond its outline
const YEAR_BUTTON_HIT_SIZE = 56;
const YEAR_BUTTON_X = 104;
const YEAR_BUTTON_Y = -29;
const YEAR_BUTTON_ICON_SIZE = 20;
const HALF_CIRCLE_BASELINE = 28;
const MAX_DOCUMENT_LANE_PITCH = 16;
const DOCUMENT_LANE_FILL = 0.75;
const MAX_WEEK_TICKS = 10;
const LEG_GAP = 2;
const FULL_CIRCLE = 2 * Math.PI;
const QUARTER_CIRCLE = Math.PI / 2;

const laneOuterRadius = (lane: number) =>
  RING_RADIUS - RING_PADDING - lane * (LANE_THICKNESS + LANE_GAP);

const laneMidRadius = (lane: number) =>
  laneOuterRadius(lane) - LANE_THICKNESS / 2;

// Text along the band turns around on the lower half, to stay the right way up
const alongBand = (angle: number) => {
  const degrees = toDegrees(angle);
  const isTurned = degrees > 90 && degrees < 270;

  return { rotation: isTurned ? degrees + 180 : degrees, isTurned };
};

const BAND_LABEL_STYLE =
  "stroke-card [paint-order:stroke] [stroke-linejoin:round]";

interface YearButtonProps {
  x: number;
  label: string;
  icon: LucideIcon;
  isFocusable: boolean;
  onPress: () => void;
}

const YearButton = ({
  x,
  label,
  icon: Icon,
  isFocusable,
  onPress,
}: YearButtonProps) => (
  <g
    role="button"
    tabIndex={isFocusable ? 0 : -1}
    aria-label={label}
    transform={`translate(${x} ${YEAR_BUTTON_Y})`}
    className="group text-foreground cursor-pointer outline-none"
    onClick={onPress}
    onKeyDown={activateOnKey(onPress)}
  >
    <rect
      x={-YEAR_BUTTON_HIT_SIZE / 2}
      y={-YEAR_BUTTON_HIT_SIZE / 2}
      width={YEAR_BUTTON_HIT_SIZE}
      height={YEAR_BUTTON_HIT_SIZE}
      className="fill-transparent"
    />
    <rect
      x={-YEAR_BUTTON_SIZE / 2}
      y={-YEAR_BUTTON_SIZE / 2}
      width={YEAR_BUTTON_SIZE}
      height={YEAR_BUTTON_SIZE}
      rx={8}
      className="fill-background stroke-border group-hover:fill-muted group-focus-visible:stroke-ring transition-colors group-focus-visible:stroke-2"
    />
    <Icon
      x={-YEAR_BUTTON_ICON_SIZE / 2}
      y={-YEAR_BUTTON_ICON_SIZE / 2}
      size={YEAR_BUTTON_ICON_SIZE}
    />
  </g>
);

interface WindowArc {
  lane: number;
  startAngle: number;
  endAngle: number;
  startsBeforeYear: boolean;
  endsAfterYear: boolean;
}

// A review window that runs on into the next year ends in an arrowhead, and one
// that comes in from the year before starts with the notch that arrowhead leaves
const windowArcPath = ({
  lane,
  startAngle,
  endAngle,
  startsBeforeYear,
  endsAfterYear,
}: WindowArc) => {
  const outerRadius = laneOuterRadius(lane);
  const innerRadius = outerRadius - LANE_THICKNESS;
  const midRadius = laneMidRadius(lane);
  const halfSweep = (endAngle - startAngle) / 2;
  const arrowhead = Math.min(ARROWHEAD_LENGTH / midRadius, halfSweep);
  const corner = Math.min(WINDOW_CORNER_RADIUS, halfSweep * innerRadius);
  const point = (angle: number, radius: number) =>
    polarPoint(angle, radius).join(" ");
  const turn = `A ${corner} ${corner} 0 0 1`;
  const largeArc = (sweep: number) => (sweep > Math.PI ? 1 : 0);

  // Each end says where the long arcs stop, and draws what joins them there
  const start = startsBeforeYear
    ? {
        outerAngle: startAngle,
        innerAngle: startAngle,
        cap: `L ${point(startAngle + arrowhead, midRadius)} L ${point(startAngle, outerRadius)}`,
      }
    : {
        outerAngle: startAngle + corner / outerRadius,
        innerAngle: startAngle + corner / innerRadius,
        cap: `${turn} ${point(startAngle, innerRadius + corner)} L ${point(startAngle, outerRadius - corner)} ${turn} ${point(startAngle + corner / outerRadius, outerRadius)}`,
      };
  const end = endsAfterYear
    ? {
        outerAngle: endAngle - arrowhead,
        innerAngle: endAngle - arrowhead,
        cap: `L ${point(endAngle, midRadius)} L ${point(endAngle - arrowhead, innerRadius)}`,
      }
    : {
        outerAngle: endAngle - corner / outerRadius,
        innerAngle: endAngle - corner / innerRadius,
        cap: `${turn} ${point(endAngle, outerRadius - corner)} L ${point(endAngle, innerRadius + corner)} ${turn} ${point(endAngle - corner / innerRadius, innerRadius)}`,
      };

  return [
    `M ${point(start.outerAngle, outerRadius)}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc(end.outerAngle - start.outerAngle)} 1 ${point(end.outerAngle, outerRadius)}`,
    end.cap,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc(end.innerAngle - start.innerAngle)} 0 ${point(start.innerAngle, innerRadius)}`,
    start.cap,
    "Z",
  ].join(" ");
};

interface Props extends ReviewWindowsChartProps {
  year: number;
  onClose: () => void;
  onYearChange: (year: number) => void;
}

export const ReviewWindowsWheel = ({
  year,
  today,
  reviewWindows,
  openedWindow,
  zoom,
  onOpen,
  events,
  highlightedEventId,
  onEventOpen,
  onClose,
  onYearChange,
}: Props) => {
  const text = useText();
  const windowStatusLabel = useWindowStatusLabel();
  const windowTooltip = useChartTooltip<ReviewWindow>();
  const documentTooltip = useChartTooltip<DocumentReview>();
  const eventTooltip = useChartTooltip<AnnualWheelEvent>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const showLabels = useElementWidth(wrapperRef) >= MIN_LABELLED_WIDTH;

  const openedDomain = openedWindow && reviewPeriodDomain(openedWindow);
  const domain = openedDomain
    ? interpolateDomain(yearDomain(year), openedDomain, zoom)
    : yearDomain(year);
  // The year fills the circle; an opened window fills its upper half
  const firstAngle = lerp(0, -QUARTER_CIRCLE, zoom);
  const lastAngle = lerp(FULL_CIRCLE, QUARTER_CIRCLE, zoom);
  const angle = timeScale(domain, [firstAngle, lastAngle]);
  // A circle's own path starts at three o'clock, a quarter turn after angle 0
  const sweep = {
    pathLength: 360,
    strokeDasharray: `${toDegrees(lastAngle - firstAngle)} 360`,
    transform: `rotate(${toDegrees(firstAngle) - 90})`,
  };

  const [yearStart, yearEnd] = yearDomain(year);
  const laneCount = Math.max(
    1,
    ...reviewWindows.map((reviewWindow) => reviewWindow.lane + 1),
  );
  const documentCount = reviewWindows.reduce(
    (sum, reviewWindow) => sum + reviewWindow.documentReviews.length,
    0,
  );

  const documentReviews = openedWindow?.documentReviews ?? [];
  const documentLanePitch = Math.min(
    MAX_DOCUMENT_LANE_PITCH,
    (RING_RADIUS - RING_PADDING - HUB_RADIUS) / documentReviews.length,
  );
  const documentLaneThickness = documentLanePitch * DOCUMENT_LANE_FILL;
  const documentLaneOuterRadius = (index: number) =>
    RING_RADIUS - RING_PADDING - index * documentLanePitch;
  const activeDocumentReview = documentTooltip.active?.datum;

  const yearInnerRadius = laneOuterRadius(laneCount - 1) - LANE_THICKNESS;
  // More lanes leave a smaller centre, which the year then shrinks to fit
  const yearHubScale = Math.min(
    1,
    (yearInnerRadius - RING_PADDING) / YEAR_HUB_RADIUS,
  );
  // Today's line and the line of an active pin both reach in to here
  const markerInnerRadius = lerp(
    yearInnerRadius - RING_PADDING,
    HUB_RADIUS,
    zoom,
  );
  const showToday = isInDomain(today, domain);
  const todayAngle = angle(today);
  const [todayX1, todayY1] = polarPoint(todayAngle, markerInnerRadius);
  const [todayX2, todayY2] = polarPoint(todayAngle, BAND_RADIUS + TICK_LENGTH);
  const [todayLabelX, todayLabelY] = polarPoint(todayAngle, TODAY_LABEL_RADIUS);

  // Measured along the circle the heads sit on, so they keep their size apart
  const eventPins = placeEventPins(
    events,
    domain,
    (date) => angle(date) * PIN_HEAD_DISTANCE,
    EVENT_PIN_HEAD_GAP,
    EVENT_PIN_HEIGHT,
  );
  // Today is labelled like the pins, and makes room among their labels as the last one
  const labels = showLabels
    ? placeWheelLabels(
        [
          ...eventPins.map(({ head }) => head / PIN_HEAD_DISTANCE),
          ...(showToday ? [todayAngle] : []),
        ],
        LABEL_RADIUS,
        EVENT_LABEL_LINE_HEIGHT,
      )
    : [];
  const todayLabel = showToday ? labels[eventPins.length] : undefined;
  const weekTickDates = openedDomain
    ? weekTicks(openedDomain, MAX_WEEK_TICKS)
    : [];

  const viewHalfWidth = showLabels
    ? LABEL_RADIUS + EVENT_LABEL_ROOM
    : VIEW_RADIUS;
  const viewHeight = lerp(
    VIEW_RADIUS * 2,
    VIEW_RADIUS + HALF_CIRCLE_BASELINE,
    zoom,
  );

  return (
    <div ref={wrapperRef} className="flex flex-col gap-4">
      {/* Names the colours of the windows, which an opened window no longer shows */}
      <ReviewWindowStatusLegend opacity={yearLabelOpacity(zoom)} />
      <svg
        viewBox={`${-viewHalfWidth} ${-VIEW_RADIUS} ${viewHalfWidth * 2} ${viewHeight}`}
        className={cn(
          "w-full self-center",
          showLabels ? "max-w-4xl" : "max-w-2xl",
        )}
      >
        <circle
          r={RING_RADIUS}
          className="stroke-border fill-none"
          {...sweep}
        />
        <circle
          r={BAND_RADIUS}
          className="stroke-border fill-none"
          {...sweep}
        />
        <line
          x1={-BAND_RADIUS}
          x2={BAND_RADIUS}
          opacity={zoom}
          className="stroke-border"
        />

        {zoom < 1 && (
          <g
            opacity={1 - zoom}
            className={cn(zoom > 0 && "pointer-events-none")}
          >
            <circle
              r={laneMidRadius(0)}
              strokeWidth={LANE_THICKNESS}
              className="stroke-foreground/5 fill-none"
              {...sweep}
            />

            {monthStarts(year).map((month) => {
              const [x1, y1] = polarPoint(angle(month), RING_RADIUS);
              const [x2, y2] = polarPoint(angle(month), BAND_RADIUS);

              return (
                isInDomain(month, domain) && (
                  <line
                    key={month.getMonth()}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    className="stroke-border"
                  />
                )
              );
            })}

            {reviewWindows.map((reviewWindow) => {
              const count = reviewWindow.documentReviews.length;
              const status = reviewWindowStatusOf(reviewWindow, today);
              const midRadius = laneMidRadius(reviewWindow.lane);
              const reviewEnd = endOfReviewWindow(reviewWindow.reviewTo);
              const from = angle(reviewWindow.reviewFrom);
              const to = angle(reviewEnd);
              const arcLength = (to - from) * midRadius;
              const windowArc = {
                lane: reviewWindow.lane,
                startAngle: from,
                endAngle: to,
                startsBeforeYear: reviewWindow.reviewFrom < yearStart,
                endsAfterYear: reviewEnd > yearEnd,
              };
              const arrowhead = ARROWHEAD_LENGTH / midRadius;
              // The count sits in the middle of the part that is as thick as the lane
              const labelFrom =
                from + (windowArc.startsBeforeYear ? arrowhead : 0);
              const labelTo = to - (windowArc.endsAfterYear ? arrowhead : 0);
              const [labelX, labelY] = polarPoint(
                (labelFrom + labelTo) / 2,
                midRadius,
              );
              const documents = text.documents(count);
              const open = () => onOpen(reviewWindow);

              if (arcLength < 1) return null;

              return (
                <g
                  key={reviewWindow.reviewFrom.getTime()}
                  role="button"
                  tabIndex={zoom > 0 ? -1 : 0}
                  aria-label={`${formatReviewPeriod(reviewWindow)}: ${documents}, ${windowStatusLabel[status]}`}
                  className="group cursor-pointer outline-none"
                  onClick={open}
                  onKeyDown={activateOnKey(open)}
                  {...windowTooltip.markProps(reviewWindow)}
                >
                  <path
                    d={windowArcPath(windowArc)}
                    className={cn(
                      "group-focus-visible:stroke-ring transition-opacity group-hover:opacity-80 group-focus-visible:stroke-2",
                      windowStatusFill[status],
                    )}
                  />
                  {(labelTo - labelFrom) * midRadius >=
                    MIN_LABELLED_ARC_LENGTH && (
                    <text
                      x={labelX}
                      y={labelY}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className={cn(
                        "pointer-events-none text-sm font-semibold",
                        countFill[status],
                      )}
                    >
                      {count}
                    </text>
                  )}
                </g>
              );
            })}

            <g
              textAnchor="middle"
              opacity={yearLabelOpacity(zoom)}
              transform={`scale(${yearHubScale})`}
            >
              <YearButton
                x={-YEAR_BUTTON_X}
                label={text.previousYear}
                icon={ChevronLeft}
                isFocusable={zoom === 0}
                onPress={() => onYearChange(year - 1)}
              />
              <text y={-8} className="fill-foreground text-6xl font-semibold">
                {year}
              </text>
              <YearButton
                x={YEAR_BUTTON_X}
                label={text.nextYear}
                icon={ChevronRight}
                isFocusable={zoom === 0}
                onPress={() => onYearChange(year + 1)}
              />
              <text y={24} className="fill-muted-foreground text-base">
                {text.documents(documentCount)}
              </text>
              <text y={46} className="fill-muted-foreground text-base">
                {text.inReviewWindows(reviewWindows.length)}
              </text>
            </g>
          </g>
        )}

        {openedWindow && openedDomain && zoom > 0 && (
          <g opacity={zoom} className={cn(zoom < 1 && "pointer-events-none")}>
            {weekTickDates.map((tick) => {
              const [x1, y1] = polarPoint(angle(tick), HUB_RADIUS);
              const [x2, y2] = polarPoint(angle(tick), BAND_RADIUS);

              return (
                <line
                  key={tick.getTime()}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  className="stroke-border"
                />
              );
            })}

            {documentReviews.map((documentReview, index) => {
              const outerRadius = documentLaneOuterRadius(index);
              const midRadius = outerRadius - documentLaneThickness / 2;
              const maxGapAngle = LEG_GAP / midRadius;
              const isDimmed =
                activeDocumentReview !== undefined &&
                activeDocumentReview !== documentReview;

              return (
                <g
                  key={documentReview.entry.id}
                  className={cn("transition-opacity", isDimmed && "opacity-30")}
                  {...documentTooltip.markProps(documentReview)}
                >
                  {/* The whole of the ring answers to the pointer, the gaps included */}
                  <path
                    d={arcLine(
                      firstAngle,
                      lastAngle,
                      outerRadius - documentLanePitch / 2,
                    )}
                    strokeWidth={documentLanePitch}
                    className="fill-none stroke-transparent"
                  />
                  {reviewLegs(documentReview).map((leg, order) => {
                    const legAngle = angle(leg.to) - angle(leg.from);
                    const gapAngle = Math.min(maxGapAngle, legAngle / 2);
                    const startAngle = angle(leg.from) + gapAngle / 2;
                    const endAngle = angle(leg.to) - gapAngle / 2;

                    if (endAngle <= startAngle) return null;

                    return (
                      <path
                        key={order}
                        d={arcLine(startAngle, endAngle, midRadius)}
                        strokeWidth={documentLaneThickness}
                        className={cn(
                          "fill-none stroke-current",
                          leg.workflowStep
                            ? workflowStepColor(order)
                            : "text-foreground/10",
                        )}
                      />
                    );
                  })}
                </g>
              );
            })}

            <g textAnchor="middle" opacity={documentLabelOpacity(zoom)}>
              <text y={-36} className="fill-foreground text-lg font-semibold">
                {formatReviewPeriodInYear(openedWindow)}
              </text>
              <text y={-14} className="fill-muted-foreground text-sm">
                {text.documents(documentReviews.length)}
              </text>
            </g>
            <circle
              r={HUB_RADIUS - RING_PADDING}
              className="cursor-pointer fill-transparent"
              onClick={onClose}
            >
              <title>{text.backToYear(year)}</title>
            </circle>
          </g>
        )}

        {showToday && (
          <g className="pointer-events-none">
            <line
              x1={todayX1}
              y1={todayY1}
              x2={todayX2}
              y2={todayY2}
              strokeWidth={1.5}
              className="stroke-foreground"
            />
            <circle
              cx={todayX2}
              cy={todayY2}
              r={3.5}
              className="fill-foreground"
            />
          </g>
        )}

        {eventPins.map(({ annualWheelEvent, tip, head }, index) => {
          const tipAngle = tip / PIN_HEAD_DISTANCE;
          const label = labels[index];

          return (
            <EventPin
              key={annualWheelEvent.id}
              annualWheelEvent={annualWheelEvent}
              tip={polarPoint(tipAngle, BAND_RADIUS)}
              head={polarPoint(head / PIN_HEAD_DISTANCE, PIN_HEAD_DISTANCE)}
              guideEnd={polarPoint(tipAngle, markerInnerRadius)}
              labelPoint={label && [label.x, label.y]}
              isHighlighted={annualWheelEvent.id === highlightedEventId}
              tooltip={eventTooltip}
              onOpen={() => onEventOpen(annualWheelEvent)}
            />
          );
        })}

        {/* On top of today's line and the lines of the pins, which cross the band */}
        <g className="pointer-events-none">
          {yearLabelOpacity(zoom) > 0 &&
            monthStarts(year).map((month) => {
              const midMonth = dayjs(month).add(15, "day").toDate();
              const labelAngle = angle(midMonth);
              const [x, y] = polarPoint(labelAngle, TICK_LABEL_RADIUS);

              return (
                isInDomain(midMonth, domain) && (
                  <text
                    key={month.getMonth()}
                    x={x}
                    y={y}
                    transform={`rotate(${alongBand(labelAngle).rotation} ${x} ${y})`}
                    textAnchor="middle"
                    dominantBaseline="central"
                    strokeWidth={4}
                    opacity={yearLabelOpacity(zoom)}
                    className={cn(
                      "fill-muted-foreground text-sm",
                      BAND_LABEL_STYLE,
                    )}
                  >
                    {formatDate(month, "month")}
                  </text>
                )
              );
            })}
          {documentLabelOpacity(zoom) > 0 &&
            weekTickDates.map((tick) => {
              const labelAngle =
                angle(tick) + WEEK_LABEL_OFFSET / TICK_LABEL_RADIUS;
              const [x, y] = polarPoint(labelAngle, TICK_LABEL_RADIUS);
              const { rotation, isTurned } = alongBand(labelAngle);

              return (
                <text
                  key={tick.getTime()}
                  x={x}
                  y={y}
                  transform={`rotate(${rotation} ${x} ${y})`}
                  textAnchor={isTurned ? "end" : "start"}
                  dominantBaseline="central"
                  strokeWidth={4}
                  opacity={documentLabelOpacity(zoom)}
                  className={cn(
                    "fill-muted-foreground text-xs",
                    BAND_LABEL_STYLE,
                  )}
                >
                  {formatDate(tick, "dayMonth")}
                </text>
              );
            })}
        </g>

        {eventPins.map(({ annualWheelEvent }, index) => {
          const label = labels[index];

          return (
            label && (
              <EventPinLabel
                key={annualWheelEvent.id}
                annualWheelEvent={annualWheelEvent}
                point={[label.x, label.y]}
                isFlipped={label.isOnLeft}
                tooltip={eventTooltip}
                onOpen={() => onEventOpen(annualWheelEvent)}
              />
            )
          );
        })}

        {showToday && todayLabel && (
          <g className="pointer-events-none">
            <line
              x1={todayX2}
              y1={todayY2}
              x2={todayLabel.x}
              y2={todayLabel.y}
              className="stroke-foreground"
            />
            <PinLabel
              point={[todayLabel.x, todayLabel.y]}
              isFlipped={todayLabel.isOnLeft}
              className="font-semibold"
            >
              {text.today}
            </PinLabel>
          </g>
        )}
        {showToday && !todayLabel && (
          <text
            x={todayLabelX}
            y={todayLabelY}
            textAnchor="middle"
            dominantBaseline="central"
            className="fill-foreground pointer-events-none text-xs font-semibold"
          >
            {text.today}
          </text>
        )}
      </svg>

      {zoom > 0 && (
        <ReviewWindowDocuments
          documentReviews={documentReviews}
          tooltip={documentTooltip}
          opacity={zoom}
        />
      )}

      {windowTooltip.active && (
        <ChartTooltip x={windowTooltip.active.x} y={windowTooltip.active.y}>
          <ReviewWindowSummary
            reviewWindow={windowTooltip.active.datum}
            statusLabel={
              windowStatusLabel[
                reviewWindowStatusOf(windowTooltip.active.datum, today)
              ]
            }
          />
        </ChartTooltip>
      )}
      {documentTooltip.active && (
        <ChartTooltip x={documentTooltip.active.x} y={documentTooltip.active.y}>
          <DocumentReviewSummary
            documentReview={documentTooltip.active.datum}
          />
        </ChartTooltip>
      )}
      {eventTooltip.active && (
        <ChartTooltip x={eventTooltip.active.x} y={eventTooltip.active.y}>
          <EventSummary annualWheelEvent={eventTooltip.active.datum} />
        </ChartTooltip>
      )}
    </div>
  );
};
